import { ChevronRightIcon } from "@chakra-ui/icons"
import { Box, Button, Flex, Heading, Img } from "@chakra-ui/react"
import { useAtom } from "jotai"
import React, { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { defaultMenuAll } from "../../common/commonConstants"
import { chevronSImg } from "../images"
import { type MenuItemInterface } from "./menu"
import { PreviouslyClickedAtom, currentMenuPage, maxHeightAtom } from "./menu.store"
import { sanitizeUrlSegment } from "./menu.support"

export interface MenuBoxProps {
  onclickLink: string
  label: string
  subMenu?: MenuBoxProps[]
  id?: number
  category?: string
}
interface MenuboxAttrProps {
  menuBox: MenuBoxProps[]
  position: {
    left: number
    top: number
  }
  type?: "mainMenu" | "subMenu"
  menuCallBack: (link: string) => void
  isMobile: boolean
  previouslyClickedLinkProp?: MenuItemInterface
  backLinkProp?: string
  onDropdownMouseEnter?: () => void
  onDropdownMouseLeave?: () => void
}

export const MenuBox = ({ menuBox, position, type, menuCallBack, isMobile, previouslyClickedLinkProp, onDropdownMouseEnter, onDropdownMouseLeave }: MenuboxAttrProps): JSX.Element => {
  const { left, top } = position

  const menuRef = React.useRef<HTMLDivElement>(null)
  const [subMenuLeft, setSubMenuLeft] = React.useState<number>(30)
  const [nextLevelSubmenu, setNextLevelSubMenu] = React.useState<MenuBoxProps[]>()
  const [getMaxHeight, setMaxHeight] = useAtom(maxHeightAtom)
  const [clearMenu, setClearMenu] = useState<boolean>(false)
  const [getPreviouslyClickedAtom, setPreviouslyClickedLinkAtom] = useAtom(PreviouslyClickedAtom)

  const handleClick = (link: string): void => {
    setClearMenu(true)
    menuCallBack(link)
  }

  // Removes any / from the end of the link (:id)
  const handleMouseEnter = (e: React.MouseEvent, nextLevelSubmenu: MenuBoxProps[] | undefined, label?: string, labelLink?: string): void => {
    const sanitizedSubmenu: MenuBoxProps[] | undefined = nextLevelSubmenu?.map(({ onclickLink, label, ...rest }) => ({
      onclickLink: sanitizeUrlSegment(onclickLink, label),
      label,
      ...rest
    }))
    setNextLevelSubMenu(sanitizedSubmenu)
    // Not clearing the menu to create an overlay effect
    if (!isMobile) {
      setClearMenu(true)
      const bb = (e.currentTarget as HTMLButtonElement).getBoundingClientRect() ?? { left: 0, top: 0, height: 0 }

      setSubMenuLeft(bb.width)
    }
    if (isMobile) {
      setPreviouslyClickedLink({ name: label ?? "", link: labelLink ?? "" })
      if (label) {
        setCurrentMobileMenuPage({ ...getCurrentMobileMenuPage, [label]: "show" })
      }
      if (previouslyClickedLinkProp) {
        setPreviouslyClickedLinkAtom([...getPreviouslyClickedAtom, previouslyClickedLinkProp.name])
      }
    }
  }

  const assignMaxHeight = (): void => {
    const computedHeight = (nextLevelSubmenu?.length ?? 0) * 45
    const minHeight = isMobile ? window.innerHeight : 300
    setMaxHeight(Math.max(computedHeight, minHeight))
  }

  const [previouslyClickedLink, setPreviouslyClickedLink] = useState<MenuItemInterface>(previouslyClickedLinkProp ?? { name: "all", link: "" })
  const [animateLeft, setAnimateLeft] = useState<number>(-280)
  const [getCurrentMobileMenuPage, setCurrentMobileMenuPage] = useAtom(currentMenuPage)
  const [backLinkProp] = useState<string>(getPreviouslyClickedAtom.at(-1) ?? getPreviouslyClickedAtom.at(0) ?? defaultMenuAll)

  useEffect(() => {
    if (clearMenu) {
      setClearMenu(false)
      assignMaxHeight()
    }
  }, [clearMenu])

  useEffect(() => {
    assignMaxHeight()
    setAnimateLeft(0)
    setCurrentMobileMenuPage({ ...getCurrentMobileMenuPage, [previouslyClickedLink.name]: "show" })
  }, [])

  useEffect(() => {
    if (isMobile && previouslyClickedLinkProp && getCurrentMobileMenuPage[previouslyClickedLinkProp.name] === "hide") {
      setAnimateLeft(-280)
    }
    if (isMobile && previouslyClickedLinkProp && getCurrentMobileMenuPage[previouslyClickedLinkProp.name] === "show") {
      setAnimateLeft(0)
    }
  }, [getCurrentMobileMenuPage])

  const menuListRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const navigateTo = (link: string): void => {
    navigate(link)
  }

  return (
    <React.Fragment key={previouslyClickedLinkProp?.name}>
      <Box
        zIndex={105}
        bg="primary"
        height={`${getMaxHeight}px`}
        shadow={isMobile ? "0px 8px 8px rgba(0, 0, 0, 0.25)" : "none"}
        pos="fixed"
        left={`${isMobile ? animateLeft : left}px`}
        transition={"0.6s"}
        transform={"translateX(0)"}
        top={top}
        color="white"
        ref={menuRef}
        {...(isMobile && { w: "280px" })}
        borderLeft={type === "subMenu" && !isMobile ? "1px solid white" : "none"}
        onMouseEnter={onDropdownMouseEnter}
        onMouseLeave={onDropdownMouseLeave}
      >
        {previouslyClickedLinkProp && (
          <Box pl="4" w="100%" bg="primary" pt="4" display={["block", "block", "block", "none"]}>
            <Button
              h="auto"
              p="0"
              variant={"transparent"}
              onClick={() => {
                setCurrentMobileMenuPage({ ...getCurrentMobileMenuPage, [previouslyClickedLinkProp.name]: "hide" })
                setPreviouslyClickedLinkAtom(getPreviouslyClickedAtom.slice(0, -1))
              }}
              fontFamily="Work Sans"
            >
              <Flex align={"center"}>
                <Img src={chevronSImg} h="22px" sx={{ transform: "rotate(90deg)" }} alt="Expand menu" />
                {backLinkProp}
              </Flex>
            </Button>
            <Heading
              pl="22px"
              mt="6"
              pb="5"
              maxW="230px"
              variant={"Heading2SemiBold"}
              onClick={() => {
                navigateTo(previouslyClickedLink.link)
              }}
              cursor="pointer"
            >
              {previouslyClickedLinkProp.name}
            </Heading>
          </Box>
        )}
        <Box overflow={"auto"} bg="primary" ref={menuListRef} maxHeight="700px" transition="500ms">
          {menuBox?.map(({ onclickLink, label, subMenu, id }, index) => (
            <React.Fragment key={label}>
              {/* mobile */}
              {isMobile && (
                <Box pl="16px" top="0" bg="primary">
                  <Flex pl="22px" align="flex-start">
                    <Button
                      variant={"RButtonBold"}
                      textAlign={"left"}
                      borderRadius={0}
                      p="0"
                      pb="3"
                      height="auto"
                      width="100%"
                      justifyContent="space-between"
                      onClick={e => {
                        if (subMenu) {
                          setNextLevelSubMenu(subMenu)
                          handleMouseEnter(e, subMenu, label, onclickLink)
                        }
                        if (!subMenu && onclickLink) {
                          handleClick(onclickLink)
                        }
                      }}
                      key={`${label}_${id ?? ""}`}
                    >
                      {label}
                    </Button>
                    {subMenu && (
                      <Button
                        variant="transparent"
                        m="0"
                        p="0"
                        ml="0"
                        h="auto"
                        w="auto"
                        onClick={e => {
                          setNextLevelSubMenu(subMenu)
                          handleMouseEnter(e, subMenu, label)
                        }}
                      >
                        <Img src={chevronSImg} h="22px" sx={{ transform: "rotate(-90deg)" }} alt="Collapse menu" />
                      </Button>
                    )}
                  </Flex>
                </Box>
              )}
              {/* Desktop */}
              {!isMobile && (
                <React.Fragment key={`${label}_${left}`}>
                  <Button
                    p="3"
                    py="3"
                    bg="primary"
                    height="auto"
                    borderTop={index === 0 ? "2px solid white" : "none"}
                    minWidth={type === "subMenu" ? "130px" : "200px"}
                    width="100%"
                    variant={"dropdownMenuButton"}
                    justifyContent="space-between"
                    onClick={() => {
                      handleClick(onclickLink)
                    }}
                    key={`${label}_${id ?? ""}`}
                    onMouseEnter={e => {
                      handleMouseEnter(e, subMenu)
                    }}
                    rightIcon={subMenu && <ChevronRightIcon />}
                  >
                    {label}
                  </Button>
                  {!clearMenu && nextLevelSubmenu?.length && subMenuLeft && (
                    <MenuBox
                      isMobile={false}
                      menuCallBack={link => {
                        menuCallBack(link)
                      }}
                      type={"subMenu"}
                      position={{ left: subMenuLeft, top: 0 }}
                      menuBox={nextLevelSubmenu}
                    />
                  )}
                </React.Fragment>
              )}
            </React.Fragment>
          ))}
        </Box>
        {!clearMenu && nextLevelSubmenu && isMobile && (
          <MenuBox
            backLinkProp={previouslyClickedLink.name}
            isMobile
            menuCallBack={link => {
              menuCallBack(link)
            }}
            type={"subMenu"}
            position={{ left: 0, top: 0 }}
            menuBox={nextLevelSubmenu}
            previouslyClickedLinkProp={previouslyClickedLink}
          />
        )}
      </Box>
    </React.Fragment>
  )
}
